'use-strict';

import PhotoSwipeLightbox from './photoswipe-lightbox.esm.min.js';
// GemSense: the PhotoSwipe core (80 KB) is fetched on the first lightbox open, not with every
// product page (pswpModule below; PhotoSwipe 5 takes a dynamic import there).

// Discrete zoom presets, expressed as a percentage of the "fit to viewport" size.
// 100% = whole image visible; 200% = twice that size.
const ZOOM_PRESETS = [100, 120, 140, 160, 200];
const ZOOM_MIN = ZOOM_PRESETS[0];
const ZOOM_MAX = ZOOM_PRESETS[ZOOM_PRESETS.length - 1];

// Product gallery zoom orchestrator.
// - inner_zoom_circle => circular hover magnifier (Drift) on desktop + click-to-open lightbox.
// - open_lightbox     => click-to-open lightbox only.
// - inner_zoom_square / external_zoom => Drift magnifier on desktop, lightbox on mobile.
class ZoomAction extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type || 'no_zoom';
    this.zoomOption = this.dataset.zoomOption || 'external';
    this.lightbox = null;
    this.drift = null; // theme.js resets this to null to force a Drift re-init after a variant change.
    this._lightboxBound = false;
    this.init();
  }

  init() {
    if (this.type === 'no_zoom') return;
    this._onResize = this.responsive.bind(this);
    window.addEventListener('resize', this._onResize);
    window.addEventListener('load', this._onResize);
    // Self-initialise so a gallery inserted after page load (e.g. a variant swap)
    // still wires up without waiting for another window 'load' event.
    this.responsive();
  }

  // Modes that show the hover magnifier (Drift) on desktop.
  get usesDrift() {
    return (
      this.type === 'inner_zoom_circle' ||
      this.type === 'inner_zoom_square' ||
      this.type === 'external_zoom'
    );
  }

  // Whether a click on a gallery image should open the lightbox at the current viewport.
  shouldOpenLightbox() {
    if (this.type === 'open_lightbox' || this.type === 'inner_zoom_circle') return true;
    // inner_zoom_square / external_zoom: lightbox is the mobile fallback only.
    return window.innerWidth < 768;
  }

  responsive() {
    const isDesktop = window.innerWidth >= 768;
    this.initLightBox();
    if (this.usesDrift && isDesktop) {
      this.initDrift();
    } else {
      this.destroyDrift();
    }
    this.updateSwiper();
  }

  // Bind the lightbox once. Clicks are delegated from the component root so they
  // keep working after the gallery markup is replaced on a variant change.
  initLightBox() {
    if (this._lightboxBound) return;
    this._lightboxBound = true;

    this.lightbox = new PhotoSwipeLightbox({
      gallery: this,
      children: 'a.media-gallery__image',
      pswpModule: () => import('./photoswipe.esm.min.js'),
      counter: false,
      zoom: false, // we provide our own zoom toolbar instead of the default button
      preloader: false,
      arrowPrev: false,
      arrowNext: false,
      close: false,
      loop: false,
      bgOpacity: 1,
      wheelToZoom: true,
      initialZoomLevel: 'fit',
      secondaryZoomLevel: (zoomLevel) => zoomLevel.fit * (ZOOM_MAX / 100),
      maxZoomLevel: (zoomLevel) => zoomLevel.fit * (ZOOM_MAX / 100),
    });

    this.lightbox.on('uiRegister', () => this.registerUI());
    this.lightbox.init();

    this.addEventListener('click', (e) => {
      const link = e.target.closest('a.media-gallery__image');
      if (!link || !this.contains(link)) return;
      e.preventDefault();
      if (!this.shouldOpenLightbox()) return;
      const items = Array.from(this.querySelectorAll('a.media-gallery__image'));
      const index = items.indexOf(link);
      this.lightbox.loadAndOpen(index < 0 ? 0 : index);
    });
  }

  registerUI() {
    const { pswp } = this.lightbox;

    pswp.ui.registerElement({
      name: 'bls--close',
      isButton: true,
      tagName: 'button',
      ariaLabel: 'Close',
      html: '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" class="transition"><use href="#icon-close"></use></svg>',
      onClick: () => pswp.close(),
    });

    pswp.ui.registerElement({
      name: 'bottomBar',
      className: 'pswp__bottom-bar',
      appendTo: 'wrapper',
      onInit: (el) => {
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'pswp__button pswp__button-next';
        next.setAttribute('aria-label', 'Next image');
        next.innerHTML = '<svg width="6" height="11" fill="none"><use href="#icon-next"></use></svg>';
        next.onclick = () => pswp.next();
        el.appendChild(next);

        const counter = document.createElement('span');
        counter.className = 'pswp__counter';
        const renderCounter = () => {
          counter.innerText =
            pswp.currIndex + 1 + (pswp.options.indexIndicatorSep || ' / ') + pswp.getNumItems();
        };
        pswp.on('change', renderCounter);
        renderCounter();
        el.appendChild(counter);

        const prev = document.createElement('button');
        prev.type = 'button';
        prev.className = 'pswp__button pswp__button-prev';
        prev.setAttribute('aria-label', 'Previous image');
        prev.innerHTML = '<svg width="6" height="11" fill="none"><use href="#icon-back"></use></svg>';
        prev.onclick = () => pswp.prev();
        el.appendChild(prev);
      },
    });

    // Discrete zoom toolbar: − / readout / + stepper plus quick-jump preset pills.
    pswp.ui.registerElement({
      name: 'zoomBar',
      className: 'pswp__zoom-bar',
      appendTo: 'wrapper',
      onInit: (el) => this.buildZoomBar(el, pswp),
    });
  }

  buildZoomBar(el, pswp) {
    const fit = () => {
      const s = pswp.currSlide;
      return s && s.zoomLevels && s.zoomLevels.fit ? s.zoomLevels.fit : 1;
    };
    const currentPct = () => {
      const s = pswp.currSlide;
      if (!s || !s.zoomLevels || !s.zoomLevels.fit) return 100;
      return Math.round((s.currZoomLevel / s.zoomLevels.fit) * 100);
    };
    const zoomToPct = (pct) => {
      const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, pct));
      const center = { x: pswp.viewportSize.x / 2, y: pswp.viewportSize.y / 2 };
      pswp.zoomTo(fit() * (clamped / 100), center, 333);
    };

    const stepper = document.createElement('div');
    stepper.className = 'pswp__zoom-stepper';

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'pswp__zoom-step';
    minus.setAttribute('aria-label', 'Zoom out');
    minus.innerHTML =
      '<svg width="14" height="2" viewBox="0 0 14 2" aria-hidden="true"><rect width="14" height="2" rx="1"/></svg>';
    minus.addEventListener('click', (e) => {
      e.stopPropagation();
      const cur = currentPct();
      const lower = [...ZOOM_PRESETS].reverse().find((p) => p < cur - 1);
      zoomToPct(lower != null ? lower : ZOOM_MIN);
    });

    const readout = document.createElement('span');
    readout.className = 'pswp__zoom-readout';
    readout.setAttribute('aria-live', 'polite');

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'pswp__zoom-step';
    plus.setAttribute('aria-label', 'Zoom in');
    plus.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><rect x="6" width="2" height="14" rx="1"/><rect y="6" width="14" height="2" rx="1"/></svg>';
    plus.addEventListener('click', (e) => {
      e.stopPropagation();
      const cur = currentPct();
      const higher = ZOOM_PRESETS.find((p) => p > cur + 1);
      zoomToPct(higher != null ? higher : ZOOM_MAX);
    });

    stepper.append(minus, readout, plus);

    const presets = document.createElement('div');
    presets.className = 'pswp__zoom-presets';
    const pills = ZOOM_PRESETS.map((p) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pswp__zoom-preset';
      b.dataset.pct = String(p);
      b.textContent = p + '%';
      b.setAttribute('aria-label', `Zoom to ${p} percent`);
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        zoomToPct(p);
      });
      presets.appendChild(b);
      return b;
    });

    el.append(stepper, presets);

    // Keep pointer interactions on the bar from reaching PhotoSwipe's
    // tap-to-close / toggle-controls handling.
    ['pointerdown', 'pointerup', 'click'].forEach((ev) =>
      el.addEventListener(ev, (e) => e.stopPropagation())
    );

    const update = () => {
      const cur = currentPct();
      readout.textContent = cur + '%';
      let nearest = ZOOM_PRESETS[0];
      let best = Infinity;
      ZOOM_PRESETS.forEach((p) => {
        const d = Math.abs(p - cur);
        if (d < best) {
          best = d;
          nearest = p;
        }
      });
      pills.forEach((b) => {
        const active = Number(b.dataset.pct) === nearest;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      minus.disabled = cur <= ZOOM_MIN + 1;
      plus.disabled = cur >= ZOOM_MAX - 1;
    };

    pswp.on('zoomPanUpdate', update);
    pswp.on('change', () => requestAnimationFrame(update));
    pswp.on('afterInit', () => requestAnimationFrame(update));
    update();
  }

  initDrift() {
    if (this.drift !== null) return;
    const allTriggers = this.querySelectorAll('.drift-trigger');
    if (allTriggers.length === 0) return;
    const paneContainer = this.closest('.sec__featured-product')?.querySelector('.zoom-external-area');
    const instances = [];
    allTriggers.forEach((trigger) => {
      const inlineContainer = trigger.closest('[data-pane-container]');
      if (!inlineContainer) return;
      const zoomOption = this.zoomOption;
      const onShow = () => inlineContainer.classList.add('relative');
      const onHide = () => inlineContainer.classList.remove('relative');
      if (zoomOption === 'inner-2') {
        instances.push(
          new Drift(trigger, {
            inlinePane: true,
            zoomFactor: 3,
            containInline: paneContainer,
            paneContainer: inlineContainer,
            hoverBoundingBox: false,
            onShow,
            onHide,
          })
        );
      } else {
        instances.push(
          new Drift(trigger, {
            inlinePane: zoomOption,
            zoomFactor: 5,
            containInline: !!paneContainer,
            paneContainer: zoomOption === 'external' ? paneContainer : inlineContainer,
            hoverBoundingBox: zoomOption === 'external',
            onShow,
            onHide,
          })
        );
      }
    });
    this.drift = instances; // non-null marker so the guard above holds until reset.
  }

  destroyDrift() {
    if (Array.isArray(this.drift)) {
      this.drift.forEach((d) => {
        try {
          d.destroy();
        } catch (e) {}
      });
    }
    this.drift = null;
  }

  updateSwiper() {
    if (typeof Swiper !== 'undefined') {
      const thumb = this.querySelector('.thumbnail-slide');
      if (thumb && thumb.swiper) thumb.swiper.update();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('load', this._onResize);
  }
}
customElements.define('zoom-action', ZoomAction);
